import React, { useMemo, useState } from "react";

export type ColumnDef<T> = {
  key: string;
  header: string;
  /** If provided, used to read raw value for sort/filter */
  accessor?: (row: T) => any;
  /** How to render the cell */
  cell?: (row: T) => React.ReactNode;
  /** Allow sorting by this column */
  sortable?: boolean;
  /** Optional class for td */
  className?: string;
};

type Props<T> = {
  title?: string;
  subtitle?: string;
  data: T[];
  columns: ColumnDef<T>[];
  /** Which keys to search in. If not provided, we search every column accessor/cell string we can. */
  searchKeys?: string[];
  /** Status filter config (optional) */
  status?: {
    key: string; // e.g. "status"
    label?: string; // e.g. "Status"
    options?: string[]; // if not provided, auto-derived from data
  };
  initialPageSize?: number;
  emptyText?: string;
};

function normalize(v: any) {
  return String(v ?? "")
    .toLowerCase()
    .trim();
}

function getValue<T>(row: T, col: ColumnDef<T>) {
  if (col.accessor) return col.accessor(row);
  // fallback for key lookup
  return (row as any)?.[col.key];
}

export default function DataTable<T>({
  title,
  subtitle,
  data,
  columns,
  searchKeys,
  status,
  initialPageSize = 10,
  emptyText = "No records found.",
}: Props<T>) {
  const [q, setQ] = useState("");
  const [statusValue, setStatusValue] = useState<string>("all");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const statusOptions = useMemo(() => {
    if (!status) return [];
    if (status.options?.length) return status.options;
    const key = status.key;
    const set = new Set<string>();
    for (const row of data) {
      const v = (row as any)?.[key];
      if (v !== null && v !== undefined && String(v).trim() !== "") {
        set.add(String(v));
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [status, data]);

  const filtered = useMemo(() => {
    let rows = [...data];

    // status filter
    if (status && statusValue !== "all") {
      rows = rows.filter(
        (r: any) => String(r?.[status.key] ?? "") === statusValue
      );
    }

    // search filter
    const query = normalize(q);
    if (query) {
      rows = rows.filter((row) => {
        // if searchKeys provided, only those keys are searched
        if (searchKeys?.length) {
          return searchKeys.some((k) =>
            normalize((row as any)?.[k]).includes(query)
          );
        }

        // else, try columns
        return columns.some((col) =>
          normalize(getValue(row, col)).includes(query)
        );
      });
    }

    // sorting
    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      if (col) {
        rows.sort((a, b) => {
          const av = getValue(a, col);
          const bv = getValue(b, col);

          // numeric sort when possible
          const an = Number(av);
          const bn = Number(bv);
          const bothNumbers = Number.isFinite(an) && Number.isFinite(bn);

          let cmp = 0;
          if (bothNumbers) {
            cmp = an - bn;
          } else {
            cmp = String(av ?? "").localeCompare(String(bv ?? ""), undefined, {
              numeric: true,
              sensitivity: "base",
            });
          }
          return sortDir === "asc" ? cmp : -cmp;
        });
      }
    }

    return rows;
  }, [data, q, status, statusValue, columns, searchKeys, sortKey, sortDir]);

  const totalRows = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  const paged = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize, totalPages]);

  // reset page when filters change
  React.useEffect(() => {
    setPage(1);
  }, [q, statusValue, pageSize]);

  const onSort = (key: string) => {
    const col = columns.find((c) => c.key === key);
    if (!col?.sortable) return;

    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <div className="border rounded-xl p-4">
      {title || subtitle ? (
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
          <div>
            {title ? (
              <div className="font-semibold text-gray-800">{title}</div>
            ) : null}
            {subtitle ? (
              <div className="text-sm text-gray-500 mt-1">{subtitle}</div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Controls */}
      <div className="mt-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search..."
          className="w-full md:max-w-sm px-3 py-2 rounded-lg border text-sm focus:outline-none"
        />

        {status ? (
          <select
            value={statusValue}
            onChange={(e) => setStatusValue(e.target.value)}
            className="w-full md:w-auto px-3 py-2 rounded-lg border text-sm bg-white"
          >
            <option value="all">{status.label ?? "Status"}: All</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        ) : null}

        <div className="flex items-center gap-2 md:ml-auto">
          <div className="text-xs text-gray-500">
            {totalRows} result{totalRows === 1 ? "" : "s"}
          </div>

          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="px-3 py-2 rounded-lg border text-sm bg-white"
          >
            {[10, 20, 30, 50].map((n) => (
              <option key={n} value={n}>
                {n}/page
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              {columns.map((c) => {
                const active = sortKey === c.key;
                const arrow = active ? (sortDir === "asc" ? " ▲" : " ▼") : "";
                return (
                  <th
                    key={c.key}
                    className={`py-2 ${
                      c.sortable
                        ? "cursor-pointer select-none hover:text-gray-700"
                        : ""
                    }`}
                    onClick={() => onSort(c.key)}
                    title={c.sortable ? "Click to sort" : undefined}
                  >
                    {c.header}
                    {c.sortable ? arrow : ""}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paged.map((row, idx) => (
              <tr key={idx} className="border-b">
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`py-3 text-gray-700 ${c.className ?? ""}`}
                  >
                    {c.cell ? c.cell(row) : String(getValue(row, c) ?? "—")}
                  </td>
                ))}
              </tr>
            ))}

            {!paged.length ? (
              <tr>
                <td colSpan={columns.length} className="py-6 text-gray-500">
                  {emptyText}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between gap-2">
        <div className="text-xs text-gray-500">
          Page {page} of {totalPages}
        </div>

        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs disabled:opacity-50"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>

          <button
            className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs disabled:opacity-50"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
